import { Alert, Card, Checkbox, Col, Collapse, Input, Popover, Radio, RadioChangeEvent, Row, Space, Spin, Tooltip } from "antd";
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../../../assets/icons/icon";
import CopyableContent from "../../../../components/copyableContent";
import CustomCollapse from "../../../../components/customCollapse";
import CustomCollapseHeader from "../../../../components/customCollapseHeader";
import Details from "../../../../components/detailsList";
import InputNum from "../../../../components/form/inputNum";
import LockSwitch from "../../../../components/form/lockSwitch";
import DividerLine from "../../../../components/lineSplit";
import { renderErrorMessage } from "../../../../components/messages/errorMessage";
import { NewCustomEquipment } from "../../../../components/modal/addCustomEquipment";
import TaxCaseStore from "../../../../contexts/taxCaseStore";
import TaxFlowStore, { NewPriceType, TaxFlow } from "../../../../contexts/taxFlowStore";
import _t from "../../../../lang/translate";
import { getNewPriceComponents } from "../../../../services/newPriceService";
import { EquipmentCatalog, NewPriceData } from "../../../../types/newPriceTypes";
import { TaxCase } from "../../../../types/taxTypes";
import { formatDateGroup } from "../../../../utilities/date";
import format from "../../../../utilities/formatNumbers";
import { safeNum } from "../../../../utilities/number";
import { isValidVin } from "../../../../utilities/stringExtraFunctions";
import EquipmentManager from "../newPrice/equipmentManager";
import NewPriceSources from "../newPrice/newPriceManager";
import { calculateEquipmentPrice, calculateNewPriceFromTaxableValue } from "../newPrice/utils/calcNewPrice";
import { applyNewPriceSelectionToEquipment, applyPackagesToEquipment } from "../newPrice/utils/mappers";
import { calcTaxableValueFromPurchasePrice, calcTaxableValueFromSalesPrice, PROFIT_MARGIN_RATE, VAT_RATE } from "../newPrice/utils/taxableValue";
import { effectivePrice } from "./util";
import { isValidNum } from "../../../../utilities/typeGuard";
import { Strict } from "../../../../types/utilTypes";


// NOTE: Actions:
//  1. resetNewPriceData      --> reset manager upon getting new server data
//  2. setSelectedNewPrice    --> select newPrice source in manager => affects input, newPriceTotal, recalculates equipmentPrice
//  3. setMountedEquipment    --> Toggles mounted equipment by code => re-calculates equipmentPrice, calc effectiveNewPriceTotal
//  4. addCustomEquipment     --> Add custom equipment => recalc equipmentPrice, calc effectiveNewPriceTotal
//  5. setPriceInput          --> Set the input fields newPriceWithoutEquipment & equipmnetPrice => calc effectiveNewPriceTotal
//  6. handleNewPriceAutoCalc --> Toggles autoCalc for newPriceWithoutEquipment: if autoCalc off => selectedNewPrice = MANUAL
//  7. handleEquipmentAutoCalc -> Toogles autoCalc for equipmentPrice => sets equipment price input to be maanger or custom value, calc effectiveNewPriceTotal
//  8. handleNewPriceType     --> Change selected newPriceType: sets newPriceType and newPrice

// taxCase id = 231377
const TAX_SYSTEM_MIGRATION_ID_CUTOFF = process.env.REACT_APP_TAX_SYSTEM_MIGRATION_ID_CUTOFF

type Props = {
  isOptimzation: boolean;
}
function Clause410Tooltip() {
  return (
    <div>
      Salgspris deaktiveres og kan ikke bruges i beregningen af afgiftspligtig værdi.
      En tekst om klausul 410 tilføjes i mailen til forhandleren, når tilbuddet publiceres.
    </div>
  )
}

function effectiveTaxableValue(taxFlow: TaxFlow) {
  const purchase = taxFlow.newPrice?.taxableValueBreakdown?.purchase?.taxableValue ?? 0;
  if (taxFlow.newPrice?.requiresClause410) return purchase
  const sales = taxFlow.newPrice?.taxableValueBreakdown?.sale?.taxableValue ?? 0;
  return Math.max(sales, purchase)
}
export default function TaxFlowNewPrice({ isOptimzation }: Props) {
  const { taxCase } = TaxCaseStore.useContainer()

  if (taxCase.vehicle?.isNew) {
    return <TaxableValueAsNewVehicle isOptimzation={isOptimzation} />
  }
  return <NewPriceAsUsedVehicle isOptimzation={isOptimzation} />
}

function TaxableValueAsNewVehicle({ isOptimzation }: Props) {
  const { taxFlow, setTaxFlow, setLocks } = TaxFlowStore.useContainer()
  const { taxCase } = TaxCaseStore.useContainer()

  function setPurchasePrice(val: unknown) {
    const num = safeNum(val)
    const purchase = calcTaxableValueFromPurchasePrice(num ?? null)

    const newTaxFlow: TaxFlow = {
      ...taxFlow,
      newPrice: {
        ...taxFlow.newPrice,
        taxableValueBreakdown: {
          ...taxFlow.newPrice?.taxableValueBreakdown,
          purchase: purchase
        }
      }
    }
    const taxableValue = effectiveTaxableValue(newTaxFlow)
    setTaxFlow({
      ...newTaxFlow,
      newPrice: {
        ...newTaxFlow.newPrice,
        taxableValue
      }
    })
  }

  function setClause(checked: boolean) {
    const newTaxFlow: TaxFlow = {
      ...taxFlow,
      newPrice: {
        ...taxFlow.newPrice,
        requiresClause410: checked,
      }
    }
    setTaxFlow({
      ...newTaxFlow,
      newPrice: {
        ...newTaxFlow.newPrice,
        taxableValue: effectiveTaxableValue(newTaxFlow),
      }
    })
  }

  async function setSalesPrice(val: unknown) {
    if (taxFlow.newPrice?.requiresClause410) return
    const num = safeNum(val)
    const sales = await calcTaxableValueFromSalesPrice(num ?? null, taxCase.vehicle)

    const newTaxFlow: TaxFlow = {
      ...taxFlow,
      newPrice: {
        ...taxFlow.newPrice,
        taxableValueBreakdown: {
          ...taxFlow.newPrice?.taxableValueBreakdown,
          sale: sales
        }
      }

    }
    const taxableValue = effectiveTaxableValue(newTaxFlow)
    setTaxFlow({
      ...newTaxFlow,
      newPrice: {
        ...newTaxFlow.newPrice,
        taxableValue
      }
    })
  }

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[8, 8]} className="mb-2">
        <Col xl={{ span: 24 }} xxl={{ span: 14 }}>
          <Card size="small" bodyStyle={{ padding: 0 }} style={{ height: "100%", marginBottom: 0, padding: 16, boxShadow: "none" }}>
            <h3>{_t("calculate", "taxable_value")}</h3>
            <Row gutter={[8, 8]}>
              <Col style={{ minWidth: 360, flex: "1 1 0%" }}>
                <Card size="small" bodyStyle={{ padding: 0 }} style={{ height: "100%", marginBottom: 0, padding: 16, boxShadow: "none", width: "100%" }}>
                  <h4>{_t("purchase_price")}</h4>
                  <Row wrap={false} className="mb-2">
                    <Col span={12} style={{ display: "flex", alignItems: "center" }}>{_t("price", "vat", " ekls. ")}</Col>
                    <Col span={12}>
                      <InputNum suffix={"Kr."}
                        value={taxFlow.newPrice?.taxableValueBreakdown?.purchase?.purchasePrice ?? undefined}
                        onChange={(v) => setPurchasePrice(v)}
                      />
                    </Col>
                  </Row>
                  <Row wrap={false} justify="space-between">
                    <Col>
                      <span>{_t("taxable_value")}</span>
                      <Popover title={_t("sales_price") + " mellemregniner"}
                        content={
                          <div>
                            <Details borderLess>
                              <Details.Item label={_t("purchase_price")}
                                value={format.price(taxFlow.newPrice?.taxableValueBreakdown?.purchase?.purchasePrice)}
                              />
                              <Details.Item label={"Avance"}
                                value={format.price(taxFlow.newPrice?.taxableValueBreakdown?.purchase?.profitMargin)}
                              />
                              <Details.Item label={_t("with_moms")}
                                value={format.price(taxFlow.newPrice?.taxableValueBreakdown?.purchase?.vatValue)}
                              />
                              <Details.Item bold label={_t("taxable_value")}
                                value={format.price(taxFlow.newPrice?.taxableValueBreakdown?.purchase?.taxableValue)}
                              />
                            </Details>
                            <div className="text-right">
                              <small>
                                {format.number(taxFlow.newPrice?.taxableValueBreakdown?.purchase?.purchasePrice, { maximumFractionDigits: 0 })}
                                {' x '}
                                {format.number(PROFIT_MARGIN_RATE)}
                                {' x '}
                                {format.number(VAT_RATE)}
                                {' = '}
                                {format.number(taxFlow.newPrice?.taxableValueBreakdown?.purchase?.taxableValue, { maximumFractionDigits: 0 })}
                              </small>
                            </div>
                          </div>

                        }
                      >
                        <Icon className="text-primary-important ml-05" size="medium" name="information-circle-outline" />
                      </Popover>
                    </Col>
                    <Col>
                      {format.price(taxFlow.newPrice?.taxableValueBreakdown?.purchase?.taxableValue)}
                    </Col>
                  </Row>
                </Card>

              </Col>
              <Col style={{ minWidth: 360, flex: "1 1 0%" }}>
                <Card size="small" bodyStyle={{ padding: 0 }} style={{ height: "100%", marginBottom: 0, padding: 16, boxShadow: "none", width: "100%" }}>
                  <Row justify="space-between" align="middle" className="mb-1">
                    <Col><h4 className="m-0">{_t("sales_price")}</h4></Col>
                    <Col>
                      <Tooltip overlayStyle={{ maxWidth: 420 }} title={<Clause410Tooltip />}>
                        <Checkbox
                          checked={!!taxFlow.newPrice?.requiresClause410}
                          onChange={(e) => setClause(e.target.checked)}
                        >
                          Klausul 410
                        </Checkbox>
                      </Tooltip>
                    </Col>
                  </Row>
                  <Row wrap={false} className="mb-2">
                    <Col span={12} style={{ display: "flex", alignItems: "center" }}>Pris inkl. moms og afgift</Col>
                    <Col span={12} style={{ position: "relative" }}>
                      <InputNum suffix={"Kr."}
                        disabled={!!taxFlow.newPrice?.requiresClause410}
                        value={taxFlow.newPrice?.taxableValueBreakdown?.sale?.salesPrice ?? undefined}
                        onChange={(v) => setSalesPrice(v)}
                      />
                      <Row wrap={false} justify="space-between" style={{
                        position: "absolute",
                        bottom: -20,
                        width: "100%"
                      }}>
                        <Col>
                          <small>{_t("of_which_tax")}</small>
                        </Col>
                        <Col>
                          <small>
                            {format.price(taxFlow.newPrice?.taxableValueBreakdown?.sale?.registrationFee)}
                          </small>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row wrap={false} justify="space-between">
                    <Col>
                      <span>{_t("taxable_value")}</span>
                      <Popover title={_t("sales_price") + " mellemregniner"}
                        content={
                          <div>
                            <Details borderLess>
                              <Details.Item label={_t("sales_price")}
                                value={format.price(taxFlow.newPrice?.taxableValueBreakdown?.sale?.salesPrice)}
                              />
                              <Details.Item label={_t("registration_fee_short")}
                                value={format.price(taxFlow.newPrice?.taxableValueBreakdown?.sale?.registrationFee)}
                              />
                              <Details.Item label={_t("taxable_value")} bold
                                value={format.price(taxFlow.newPrice?.taxableValueBreakdown?.sale?.taxableValue)}
                              />
                            </Details>
                            <div className="text-right">
                              <small>
                                {format.number(taxFlow.newPrice?.taxableValueBreakdown?.sale?.salesPrice, { maximumFractionDigits: 0 })}
                                {' - '}
                                {format.number(taxFlow.newPrice?.taxableValueBreakdown?.sale?.registrationFee, { maximumFractionDigits: 0 })}
                                {' = '}
                                {format.number(taxFlow.newPrice?.taxableValueBreakdown?.sale?.taxableValue, { maximumFractionDigits: 0 })}
                              </small>
                            </div>

                          </div>
                        }
                      >
                        <Icon className="text-primary-important ml-05" size="medium" name="information-circle-outline" />
                      </Popover>
                    </Col>
                    <Col>
                      {format.price(taxFlow.newPrice?.taxableValueBreakdown?.sale?.taxableValue)}
                    </Col>
                  </Row>
                </Card>

              </Col>
            </Row>
          </Card>
        </Col>
        <Col xl={{ span: 24 }} xxl={{ span: 10 }}>
          <Card size="small" bodyStyle={{ padding: 0 }} style={{ height: "100%", marginBottom: 0, padding: 16, boxShadow: "none" }}>
            <h3>{_t("choose", "taxable_value")}</h3>
            <Radio.Group className="newprice-sources-radio w-100"
              value={"TAXABLE_VALUE"}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Radio value={"TAXABLE_VALUE"}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #EFF3F5",
                    borderRadius: "4px",
                  }}
                >
                  <Row justify="space-between">
                    <Col>Beregnet nypris inkl. ekstraudstyr</Col>
                    <Col className="text-bold">
                      <CopyableContent contentToCopy={taxFlow.newPrice?.taxableValue?.toFixed(0) ?? ""}>
                        {format.price(taxFlow.newPrice?.taxableValue)}
                      </CopyableContent>
                    </Col>
                  </Row>
                </Radio>
                <Alert type="info" message={"Vær opmærksom på, om værdien er tilsvarende markedspris."} />
              </Space>
            </Radio.Group>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

function NewPriceAsUsedVehicle({ isOptimzation }: Props) {
  const { taxFlow, setTaxFlow, setLocks } = TaxFlowStore.useContainer()
  const { taxCase } = TaxCaseStore.useContainer()
  const [loadingNewPrice, setLoadingNewPrice] = useState(false)
  const autoCalc = taxFlow.autoCalc
  const newPriceData = taxFlow.newPrice?.manager?.data
  const newPrices = taxFlow.newPrice?.manager?.data.newPrices
  const equipment = taxFlow.newPrice?.manager?.data.equipment
  const selectedNewPrice = taxFlow.newPrice?.manager?.data?.selectedNewPrice
  const newPrice = newPrices?.find(np => np.id === selectedNewPrice)
  const didMountRef = useRef(false);
  const vehicleRef = useRef(taxCase.vehicle);

  const equipmentPrice = taxFlow.newPrice?.manager?.values.equipmentPrice
  const newPriceAmount = taxFlow.newPrice?.manager?.values.newPrice
  const newPriceTotal = (taxFlow.newPrice?.equipmentPrice ?? 0) + (taxFlow.newPrice?.newPriceWithoutEquipment ?? 0)
  const { calcType, canFetch, requestKey } = conditions(taxCase, isOptimzation)
  const lastRequestKey = useRef(requestKey)

  function checkNeedsBatteryCapacity() {
    if (!!taxCase.vehicle?.emissionAmount) return false
    if (moment(taxCase.vehicle?.firstRegDate).year() > 2024) return false
    return !taxCase.vehicle?.batteryCapacity;
  }
  const needsBatteryCapacity = useMemo(checkNeedsBatteryCapacity, [taxCase.vehicle?.firstRegDate, taxCase.vehicle?.batteryCapacity])
  const taxableValueDisabled = needsBatteryCapacity || !taxFlow.newPrice?.taxableValueNewPrice || !isValidVin(taxCase.vehicle?.vin);

  function didKeyVehicleFieldsChange() {
    return vehicleRef.current?.emissionAmount !== taxCase.vehicle?.emissionAmount
      || vehicleRef.current?.typeId !== taxCase.vehicle?.typeId
      || vehicleRef.current?.firstRegDate !== taxCase.vehicle?.firstRegDate
      || vehicleRef.current?.batteryCapacity !== taxCase.vehicle?.batteryCapacity
      || vehicleRef.current?.emissionUnit !== taxCase.vehicle?.emissionUnit
  }
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (!didKeyVehicleFieldsChange()) {
      return
    }
    vehicleRef.current = taxCase.vehicle;
    try {
      const needsBatteryCapacity = checkNeedsBatteryCapacity()
      if (taxCase.vehicle?.emissionAmount === 0 && !needsBatteryCapacity) {
        // can calculate
        setTaxFlow((prev) => {
          const np = prev.newPrice?.newPriceWithoutEquipment
          const equipment = prev.newPrice?.manager?.data.equipment
          const newPriceTaxableValue = calculateNewPriceFromTaxableValue(np ?? 0, equipment ?? [], taxCase.vehicle)
          const newTaxFlow: TaxFlow = {
            ...prev,
            newPrice: {
              ...prev.newPrice,
              taxableValueNewPrice: newPriceTaxableValue,
            }
          }
          return newTaxFlow
        })
      } else {
        setTaxFlow((prev) => {
          const newTaxFlow: TaxFlow = {
            ...prev,
            newPrice: {
              ...prev.newPrice,
              newPriceType: "NEW_PRICE",
              taxableValueNewPrice: null
            }
          }
          return newTaxFlow;
        })
      }
    } catch (error) {
      renderErrorMessage(error)
    }
  }, [taxCase.vehicle])

  const resetNewPriceData = useCallback((data: NewPriceData) => {
    const newPriceAmount = data.newPrices?.find(item => item.id === data.selectedNewPrice)?.amount
    const equipmentPrice = calculateEquipmentPrice(data.equipment)
    const equipment = data.equipment;

    const effectiveNP = effectivePrice(!!autoCalc?.newPriceWithoutEquipment, newPriceAmount ?? null, taxFlow.newPrice?.newPriceWithoutEquipment ?? null)
    const effectiveEq = effectivePrice(!!autoCalc?.equipmentPrice, equipmentPrice, taxFlow.newPrice?.equipmentPrice ?? null)
    const newPriceTaxableValue = calculateNewPriceFromTaxableValue(effectiveNP ?? 0, equipment, taxCase.vehicle)
    const newPriceTotal = (effectiveNP ?? 0) + (effectiveEq ?? 0);

    let newPriceType: NewPriceType = "NEW_PRICE";
    if (newPriceTaxableValue?.newPrice && newPriceTaxableValue.newPrice < newPriceTotal && !taxableValueDisabled) {
      newPriceType = "TAXABLE_VALUE"
    }

    setTaxFlow((prev) => ({
      ...prev,
      newPrice: {
        ...prev.newPrice,
        newPriceType: newPriceType,
        taxableValueNewPrice: newPriceTaxableValue,
        equipmentPrice: prev.autoCalc?.equipmentPrice ? effectiveEq ?? undefined : prev.newPrice?.equipmentPrice,
        newPriceWithoutEquipment: prev.autoCalc?.newPriceWithoutEquipment ? effectiveNP ?? undefined : prev.newPrice?.newPriceWithoutEquipment,
        newPrice: newPriceTotal,
        manager: {
          ...prev.newPrice?.manager,
          data,
          values: {
            newPrice: newPriceAmount ?? null,
            equipmentPrice: equipmentPrice,
          },
          meta: {
            fetchedKey: requestKey
          }
        }
      }
    }))
  }, [requestKey, setTaxFlow])

  function setSelectedNewPrice(id: string | null) {
    if (!id) return;
    setTaxFlow((prev) => {
      const newPrice = prev.newPrice?.manager?.data.newPrices?.find(item => item.id === id);
      const newPriceAmount = newPrice?.amount;

      // When a new newPrice source has been select, the equipment needs to be recalculated because of the equipment included in price
      let newEquipment = applyNewPriceSelectionToEquipment(newPrice ?? null, prev.newPrice?.manager?.data.equipment ?? []);
      newEquipment = applyPackagesToEquipment(newEquipment)

      const equipmentPrice = calculateEquipmentPrice(newEquipment)

      let npAutoCalc = true;
      if (id === 'MANUAL') {
        npAutoCalc = false
      }
      const effectiveNP = effectivePrice(npAutoCalc, newPriceAmount ?? null, taxFlow.newPrice?.newPriceWithoutEquipment ?? null)
      const effectiveEq = effectivePrice(!!autoCalc?.equipmentPrice, equipmentPrice, taxFlow.newPrice?.equipmentPrice ?? null)
      const newPriceTaxableValue = calculateNewPriceFromTaxableValue(effectiveNP ?? 0, newEquipment, taxCase.vehicle)
      const newPriceTotal = (effectiveNP ?? 0) + (effectiveEq ?? 0)
      let newPriceType: NewPriceType = "NEW_PRICE";
      if (newPriceTaxableValue?.newPrice && newPriceTaxableValue.newPrice < newPriceTotal && !taxableValueDisabled) {
        newPriceType = "TAXABLE_VALUE"
      }
      const newTaxFlow: TaxFlow = {
        ...prev,
        autoCalc: {
          ...prev.autoCalc,
          newPriceWithoutEquipment: npAutoCalc,
        },
        newPrice: {
          ...prev.newPrice,
          taxableValueNewPrice: newPriceTaxableValue,
          equipmentPrice: effectiveEq ?? undefined,
          newPriceWithoutEquipment: effectiveNP ?? undefined,
          newPrice: (effectiveNP ?? 0) + (effectiveEq ?? 0),
          newPriceType,
          manager: {
            ...prev.newPrice?.manager,
            data: {
              newPrices: prev.newPrice?.manager?.data.newPrices ?? [],
              equipment: newEquipment,
              selectedNewPrice: id,
            },
            values: {
              newPrice: newPrice?.amount ?? null,
              equipmentPrice: equipmentPrice,
            }
          }
        }
      }
      return newTaxFlow;
    })
  }

  function setMountedEquipment(code: string, mounted: boolean) {
    setTaxFlow((prev) => {
      let isPkg = false;
      let equipment = prev.newPrice?.manager?.data.equipment?.map(eq => {
        if (eq.code === code) {
          if (eq.packageContent.length) {
            isPkg = true
          }
          return {
            ...eq,
            mounted: mounted
          }
        }
        return eq;
      }) ?? []
      if (isPkg) {
        equipment = applyPackagesToEquipment(equipment);
      }
      const equipmentPrice = calculateEquipmentPrice(equipment)

      const effectiveNP = effectivePrice(!!autoCalc?.newPriceWithoutEquipment, newPriceAmount ?? null, taxFlow.newPrice?.newPriceWithoutEquipment ?? null)
      const effectiveEq = effectivePrice(!!autoCalc?.equipmentPrice, equipmentPrice, taxFlow.newPrice?.equipmentPrice ?? null)
      const newPriceTaxableValue = calculateNewPriceFromTaxableValue(effectiveNP ?? 0, equipment, taxCase.vehicle)

      const newTaxFlow: TaxFlow = {
        ...prev,
        newPrice: {
          ...prev.newPrice,
          equipmentPrice: effectiveEq ?? undefined,
          newPrice: (effectiveNP ?? 0) + (effectiveEq ?? 0),
          taxableValueNewPrice: newPriceTaxableValue,
          manager: {
            ...prev.newPrice?.manager,
            data: {
              ...prev.newPrice?.manager?.data,
              newPrices: prev.newPrice?.manager?.data?.newPrices ?? [],
              selectedNewPrice: prev.newPrice?.manager?.data.selectedNewPrice ?? null,
              equipment: equipment
            },
            values: {
              ...prev.newPrice?.manager?.values,
              newPrice: prev.newPrice?.manager?.values.newPrice ?? null,
              equipmentPrice: equipmentPrice
            }
          }
        }
      }
      return newTaxFlow

    })
  }

  function addCustomEquipment(customEquipment: NewCustomEquipment) {
    const existingIndex = equipment?.findIndex(item => item.code?.toLowerCase() === customEquipment.code?.toLowerCase())
    if (existingIndex && equipment && existingIndex >= 0 && !equipment[existingIndex].isCustom) {
      // Check if the existing equipment is custom, then override, otherwise throw error
      throw new Error(_t("msg.equipment_code_already_exists"))
    }

    setTaxFlow((prev) => {
      const equipment = [...prev.newPrice?.manager?.data?.equipment ?? []];
      const newEq: EquipmentCatalog = {
        code: customEquipment.code,
        name: customEquipment.name,
        category: customEquipment.category,
        amount: customEquipment.price,
        source: "CUSTOM",
        sourceId: null,
        isWithTax: true,
        mounted: true,

        isCustom: true,

        isIncludedInMountedPackage: false,
        isVinEquipment: false,
        packageContent: [],
        includedInNewPrice: false,
        includedInPackage: [],
        isForceMounted: false,
        isForceMountedByNewPrice: false,
        isForceMountedByPackage: false
      }

      if (existingIndex && existingIndex >= 0) {
        equipment[existingIndex] = newEq
      } else {
        equipment.push(newEq)
      }

      const equipmentPrice = calculateEquipmentPrice(equipment)

      const effectiveNP = effectivePrice(!!autoCalc?.newPriceWithoutEquipment, newPriceAmount ?? null, taxFlow.newPrice?.newPriceWithoutEquipment ?? null)
      const effectiveEq = effectivePrice(!!autoCalc?.equipmentPrice, equipmentPrice, taxFlow.newPrice?.equipmentPrice ?? null)
      const newPriceTaxableValue = calculateNewPriceFromTaxableValue(effectiveNP ?? 0, equipment, taxCase.vehicle)

      const newTaxFlow: TaxFlow = {
        ...prev,
        newPrice: {
          ...prev.newPrice,
          equipmentPrice: effectiveEq ?? undefined,
          taxableValueNewPrice: newPriceTaxableValue,
          manager: {
            ...prev.newPrice?.manager,
            data: {
              ...prev.newPrice?.manager?.data,
              newPrices: prev.newPrice?.manager?.data.newPrices ?? [],
              selectedNewPrice: prev.newPrice?.manager?.data.selectedNewPrice ?? null,
              equipment
            },
            values: {
              ...prev.newPrice?.manager?.values,
              newPrice: prev.newPrice?.manager?.values.newPrice ?? null,
              equipmentPrice: equipmentPrice,
            }
          }
        }
      }
      return newTaxFlow
    })
  }

  function setNote(val: string) {
    setTaxFlow(prev => ({
      ...prev,
      newPrice: {
        ...prev.newPrice,
        note: val
      }
    }))
  }
  function setPriceInput(val: number | string | null, key: keyof Strict<TaxFlow>['newPrice']) {
    setTaxFlow((prev) => {
      const np = key === 'newPriceWithoutEquipment' ? val : prev.newPrice?.newPriceWithoutEquipment;
      const equipmentPrice = key === 'equipmentPrice' ? val : prev.newPrice?.equipmentPrice;
      const newPriceTaxableValue = calculateNewPriceFromTaxableValue(safeNum(np) ?? 0, prev.newPrice?.manager?.data?.equipment ?? [], taxCase.vehicle)

      const newPriceTotal = (safeNum(equipmentPrice) ?? 0) + (safeNum(np) ?? 0);
      let newPriceType: NewPriceType = "NEW_PRICE";
      if (newPriceTaxableValue?.newPrice && newPriceTaxableValue.newPrice < newPriceTotal && !taxableValueDisabled) {
        newPriceType = "TAXABLE_VALUE"
      }
      const newTaxFlow: TaxFlow = {
        ...prev,
        newPrice: {
          ...prev.newPrice,
          [key]: safeNum(val) ?? undefined,
          taxableValueNewPrice: newPriceTaxableValue,
          newPriceType
        }
      }

      return newTaxFlow
    })
  }




  const handleFetch = useCallback(async () => {
    if (!canFetch) return;
    try {
      setLoadingNewPrice(true);
      const data = await getNewPriceComponents(
        taxCase,
        calcType,
        newPriceData ?? null
      );
      resetNewPriceData(data);
    } catch (error) {
      renderErrorMessage(error);
    } finally {
      setLoadingNewPrice(false);
    }
  }, [canFetch, taxCase, calcType, newPriceData, resetNewPriceData]);

  useEffect(() => {
    const hasData = !!taxFlow.newPrice?.manager?.data.equipment?.length;

    if (TAX_SYSTEM_MIGRATION_ID_CUTOFF && taxCase.id < parseInt(TAX_SYSTEM_MIGRATION_ID_CUTOFF)) {
      return;
    }
    if (!requestKey) return;
    if (loadingNewPrice) return

    const requestChanged = lastRequestKey.current !== requestKey
    const shouldFetch = !hasData || requestChanged

    if (shouldFetch) {
      handleFetch()
      lastRequestKey.current = requestKey
    }
  }, [
    requestKey,
    taxFlow?.newPrice?.manager?.data?.equipment?.length,
  ])

  function handleNewPriceAutoCalc(autoCalcNP: boolean) {
    setTaxFlow((prev) => {
      const newTaxFlow: TaxFlow = {
        ...prev,
        autoCalc: {
          ...prev.autoCalc,
          newPriceWithoutEquipment: autoCalcNP
        },
        newPrice: {
          ...prev.newPrice,
          manager: {
            ...prev.newPrice?.manager,
            values: {
              ...prev.newPrice?.manager?.values,
              newPrice: prev.newPrice?.manager?.values.newPrice ?? null,
              equipmentPrice: prev.newPrice?.manager?.values.equipmentPrice ?? null,
            },
            data: {
              ...prev.newPrice?.manager?.data,
              newPrices: prev.newPrice?.manager?.data.newPrices ?? [],
              equipment: prev.newPrice?.manager?.data.equipment ?? [],
              selectedNewPrice: !autoCalcNP ? "MANUAL" : prev.newPrice?.manager?.data?.selectedNewPrice ?? null,
            }
          }
        }
      }

      return {
        ...newTaxFlow,
      }
    })
  }

  function handleEquipmentAutoCalc(autoCalcEquipment: boolean) {
    if (autoCalcEquipment) {
      const equipmentPrice = taxFlow.newPrice?.manager?.values.equipmentPrice
      const newPriceAmount = taxFlow.newPrice?.manager?.values.newPrice


      const effectiveEq = effectivePrice(!!autoCalcEquipment, equipmentPrice ?? null, taxFlow.newPrice?.equipmentPrice ?? null)
      const newTaxFlow: TaxFlow = {
        ...taxFlow,
        autoCalc: {
          ...taxFlow.autoCalc,
          equipmentPrice: autoCalcEquipment,
        },
        newPrice: {
          ...taxFlow.newPrice,
          equipmentPrice: effectiveEq ?? undefined,
          // newPriceWithoutEquipment: effectiveNP ?? undefined,
        }
      }
      setTaxFlow(newTaxFlow)
    } else {
      setLocks({ equipmentPrice: autoCalcEquipment })
    }
  }

  // TODO: Calculate newPrice from taxableValue
  // calculateNewPriceFromTaxableValue(selectedNewPrice: number, equipment: CarApiEquipment[], vehicle?: Vehicle | null)
  function handleNewPriceType(e: RadioChangeEvent) {
    setTaxFlow((prev) => {
      return {
        ...prev,
        newPrice: {
          ...prev.newPrice,
          newPriceType: e.target.value as NewPriceType,
        }
      }
    })
  }

  return (
    <div style={{ padding: 16 }}>
      <Row gutter={[8, 8]} className="mb-2">
        {/* TODO: Responsive sizes */}
        <Col span={14}>
          <Card size="small" bodyStyle={{ padding: 0 }} style={{ height: "100%", marginBottom: 0, padding: 16, boxShadow: "none" }}>
            <h4>{_t("values")}</h4>
            <Row wrap={false} className="mb-05">
              <Col span={8}>{_t("new_price")}</Col>
              <Col flex="auto">
                <InputNum disabled={autoCalc?.newPriceWithoutEquipment}
                  value={taxFlow.newPrice?.newPriceWithoutEquipment}
                  onChange={(v) => setPriceInput(v, 'newPriceWithoutEquipment')}
                />
              </Col>
              <Col flex="49px" className="text-right">
                <LockSwitch value={autoCalc?.newPriceWithoutEquipment} onChange={handleNewPriceAutoCalc} />
              </Col>
            </Row>

            <Row wrap={false} className="mb-05">
              <Col span={8}>
                {_t("equipment_price")}
              </Col>
              <Col flex="auto">
                <InputNum disabled={autoCalc?.equipmentPrice}
                  value={taxFlow.newPrice?.equipmentPrice}
                  onChange={(v) => setPriceInput(v, 'equipmentPrice')}
                />
              </Col>
              <Col flex="49px" className="text-right">
                <LockSwitch value={autoCalc?.equipmentPrice} onChange={handleEquipmentAutoCalc} />
              </Col>
            </Row>
            <Input
              placeholder="Note"
              value={taxFlow.newPrice?.note}
              onChange={(v) => setNote(v.target.value)}
            />
          </Card>
        </Col>
        <Col span={10}>
          <Card size="small" bodyStyle={{ padding: 0 }} style={{ height: "100%", marginBottom: 0, padding: 16, boxShadow: "none" }}>
            <h4>{_t("choose", "new_price")}</h4>
            <Radio.Group className="newprice-sources-radio w-100"
              value={taxFlow.newPrice?.newPriceType}
              onChange={handleNewPriceType}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Radio value={"NEW_PRICE"}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #EFF3F5",
                    borderRadius: "4px",
                  }}
                >
                  <Row justify="space-between">
                    <Col>Nypris inkl. ekstraudstyr</Col>
                    <Col className="text-bold">
                      <CopyableContent contentToCopy={newPriceTotal.toString()}>
                        {format.price(newPriceTotal)}
                      </CopyableContent>
                    </Col>
                  </Row>
                </Radio>
                <Radio value={"TAXABLE_VALUE"}
                  disabled={taxableValueDisabled}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #EFF3F5",
                    borderRadius: "4px",
                  }}
                >
                  <Row justify="space-between">
                    <Col>
                      <Space size={'small'}>
                        <span>
                          Afgiftspligtig værdi
                        </span>
                        <Popover
                          title="Hvordan beregnes nyprisen?"
                          content={
                            <div style={{ maxWidth: 320 }}>
                              <p>
                                Nyprisen beregnes ud fra den <strong>afgiftspligtige værdi</strong>
                                {' '}samt udstyrsprisen <strong>uden registreringsafgift</strong>.
                              </p>

                              <p>
                                Kun monteret udstyr fra udstyrsmanageren indgår i beregningen.
                                Systemet forsøger automatisk at fratrække afgift på udstyr,
                                hvor det er muligt.
                              </p>

                              <div style={{
                                marginTop: 12,
                                padding: 8,
                                background: "#fafafa",
                                borderRadius: 6,
                                fontSize: 13
                              }}>
                                <strong>Forudsætning:</strong><br />
                                Beregningen kan kun anvendes på elbiler med
                                1. registrering fra 2021 og frem.
                              </div>
                            </div>
                          }
                        >
                          <Icon name="information-circle-outline" size="large" />
                        </Popover>
                      </Space>
                    </Col>
                    <Col className="text-bold">
                      <CopyableContent contentToCopy={taxFlow.newPrice?.taxableValueNewPrice?.newPrice?.toFixed() ?? ""}>
                        {format.price(taxFlow.newPrice?.taxableValueNewPrice?.newPrice)}
                      </CopyableContent>
                    </Col>
                  </Row>
                  {taxFlow.newPrice?.taxableValueNewPrice?.newPrice && (
                    <Row justify="space-between" className="mt-03 text-tiny muted">
                      <Col>
                        <span>afg: {format.integer(taxFlow.newPrice?.taxableValueNewPrice.registrationFee)}</span>
                        {", "}
                        <span>udstyr: {format.integer(taxFlow.newPrice?.taxableValueNewPrice.equipmentSum)}</span>
                      </Col>
                      <Col>
                        <span>
                          {format.integer(taxFlow.newPrice?.newPriceWithoutEquipment)}
                        </span>
                        {" + "}
                        <span>
                          {format.integer(taxFlow.newPrice.taxableValueNewPrice.registrationFee)}
                        </span>
                        {" + "}
                        <span>
                          {format.integer(taxFlow.newPrice?.taxableValueNewPrice?.equipmentSum)}
                        </span>
                        {' = '}
                        <span>
                          {format.integer(taxFlow.newPrice?.taxableValueNewPrice?.newPrice)}
                        </span>
                      </Col>
                    </Row>
                  )}
                </Radio>
              </Space>
            </Radio.Group>
            {needsBatteryCapacity && (
              <p className="text-danger text-smallest mb-0 mt-03">
                Batterikapacitet mangler. For elbiler med 1. registreringsdato før 2025 kræves batterikapacitet for at beregne nyprisen via afgiftspligtig værdi.
              </p>
            )}

          </Card>
        </Col>
      </Row>

      <DividerLine><small>{_t("catalogs")}</small></DividerLine>

      <Spin spinning={loadingNewPrice}>
        <CustomCollapse style={{ margin: 0 }} defaultActiveKey={["1", "2"]}>
          <Collapse.Panel header={
            <CustomCollapseHeader style={{ width: "100%" }}>
              {_t("newPriceCatalog")}
              <span className="ml-05 text-tiny text-gray">
                - {_t("source")}:{' '}
                <strong>{printSource(newPrice?.source)} </strong>
                &bull;
                {newPrice?.dateGroup && (
                  <>
                    {' '}{formatDateGroup(newPrice?.dateGroup ?? "")} &bull;
                  </>
                )}
                {newPrice?.note && (
                  <>
                    {' '}{newPrice?.note} &bull;
                  </>
                )}
                {isValidNum(newPrice?.amount) && (
                  <span onClick={e => e.stopPropagation()}>
                    <CopyableContent contentToCopy={newPrice?.amount.toString()}>
                      {' '}{format.price(newPrice?.amount)}
                    </CopyableContent>
                  </span>
                )}
              </span>
            </CustomCollapseHeader>
          } key="1">
            <NewPriceSources newPrices={newPrices ?? null} selectedNewPrice={selectedNewPrice ?? null} setSelectedNewPrice={setSelectedNewPrice}
              equipment={equipment ?? []}
              vehicle={{
                firstRegDate: taxCase.vehicle?.firstRegDate ?? null,
                motorAtlasId: taxCase.vehicle?.motorAtlasId ?? null,
                vin: taxCase.vehicle?.vin ?? null,
                typeId: taxCase.vehicle?.typeId
              }}

            />
          </Collapse.Panel>

          <Collapse.Panel header={
            <CustomCollapseHeader style={{ width: "100%" }}>
              <Row justify="space-between">
                <Col>
                  {_t("equipmentCatalog")}
                  <span className="ml-05 text-tiny text-gray">- Konfigureret fra kilde: <strong>{printSource(newPrice?.source)} (Datogruppe {formatDateGroup(newPrice?.dateGroup ?? "")})</strong></span>
                </Col>
                <Col onClick={e => e.stopPropagation()}>
                  <CopyableContent contentToCopy={String(equipmentPrice ?? 0)}>
                    {format.price(equipmentPrice)}
                  </CopyableContent>
                </Col>
              </Row>
            </CustomCollapseHeader>
          } key="2">
            <EquipmentManager
              equipment={equipment ?? []}
              setMounted={setMountedEquipment}
              addCustomEquipment={addCustomEquipment}
              reloadData={handleFetch}
              vehicle={{
                firstRegDate: taxCase.vehicle?.firstRegDate ?? null,
                motorAtlasId: taxCase.vehicle?.motorAtlasId ?? null,
                vin: taxCase.vehicle?.vin ?? null,
                type: taxCase.vehicle?.typeId
              }}
            />
          </Collapse.Panel>
        </CustomCollapse>
      </Spin>
    </div>
  )
}
function printSource(source: unknown) {
  if (source === "MANUAL") return "Manuel"
  return source
}
function conditions(taxCase: TaxCase, isOptimzation: boolean) {

  const calcType: "optimization" | "preAssessment" = isOptimzation ? "optimization" : "preAssessment";
  const vin = taxCase.vehicle?.vin ?? null;
  const firstRegDate = taxCase.vehicle?.firstRegDate ?? null;
  const motorAtlasId = taxCase.vehicle?.motorAtlasId ?? null;
  const canFetch = (isValidVin(vin) || !!motorAtlasId) && !!firstRegDate
  const requestKey = canFetch ? `${calcType}|${vin}|${firstRegDate}|${motorAtlasId ?? ""}` : null;

  return { calcType, canFetch, requestKey }
}

